import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { FeatureContext } from 'core/context/feature.context';
import { Breakpoints } from 'core/enums/breakpoints';
import { FeatureFlag } from 'core/enums/feature-flag';
import { usePlatform } from 'core/hooks/platform';
import { BaseRoute } from 'core/models/base-routes';
import { GlobalUIStore } from 'core/stores/global-ui.store';
import { Util } from 'utils/common.utils';
import { _noop } from 'utils/function.utils';
import { Accordion } from '../../accordion/accordion';
import { AccordionItem } from '../../accordion/accordion-item';
import { AmountToPay } from '../../../account/amount-to-pay/amount-to-pay';
import { AmountToPayLayout } from '../../../account/amount-to-pay/amount-to-pay.store';
import { CardThemeType } from '../../cards/card-type';
import { FeaturePanel } from '../../panels/feature-panel/feature-panel';
import DevTools from '../../../playground/dev-tools/dev-tools';
import { ProfileCard } from '../../../profile/profile-card/profile-card';
import styles from './side-menu.module.scss';

export const SideMenu = observer(
  ({
    routes,
    activeRoute,
    onChangeActiveMenuItem = _noop,
    isAuthenticated = false,
  }: {
    routes: BaseRoute[];
    activeRoute: BaseRoute | null;
    onChangeActiveMenuItem?: (name: string | null) => void;
    isAuthenticated: boolean;
  }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const featureContext = useContext(FeatureContext);
    const featureStore = useLocalObservable(() => featureContext);

    const uiStore = useLocalObservable(() => GlobalUIStore.GetInstance);
    const baseRoute = activeRoute?.parent ? activeRoute?.parent : activeRoute;
    const { translate } = usePlatform();

    const store = useLocalObservable(() => ({
      activeRoute: baseRoute,
      selected: baseRoute,
      isOpen: false,
      _isAccountLoading: true,
      _isSubscriptionsLoading: true,
      changeIsOpen(isOpen: boolean) {
        this.isOpen = isOpen;
      },
      changeSelected(selected: BaseRoute | null) {
        this.selected = selected;
      },
      changeActiveRoute(activeRoute: BaseRoute | null) {
        this.activeRoute = activeRoute;
        onChangeActiveMenuItem(activeRoute?.name || null);
      },
    }));

    useEffect(() => {
      if (activeRoute?.name && activeRoute?.name !== store.activeRoute?.name) {
        activeRoute.isActive = true;
        store.changeActiveRoute(activeRoute || null);
        store.changeSelected(activeRoute || null);
      }
    }, [store, activeRoute]);

    // Event Handlers
    const handleMainMenuItemClick = (route: BaseRoute) => {
      if (route.isActive && route?.children?.length) {
        store.changeActiveRoute(route);

        if (store.selected) {
          onChangeActiveMenuItem(store.selected.name);

          if (route.name === store.activeRoute?.name) {
            store.changeActiveRoute(store.selected);
          }
        }
        return;
      }

      store.changeActiveRoute(route);

      if (!route?.children?.length) {
        store.changeSelected(route);
        store.changeIsOpen(false);

        navigate(
          route?.path
            ? route?.path?.replace('*', '')
            : route?.children?.length
            ? route?.children[0].path?.replace('*', '') || '/'
            : '/',
          {
            replace: true,
          }
        );
      }
    };

    const handleSubMenuItemClick = (e: React.MouseEvent, route: BaseRoute, subRoute: BaseRoute) => {
      e.preventDefault();
      e.stopPropagation();

      store.changeActiveRoute(route);
      store.changeSelected(route);
      store.changeIsOpen(false);
      navigate(subRoute?.path?.replace('*', '') || '/', {
        replace: true,
      });
    };

    const handleProfileClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      store.changeActiveRoute(null);
      store.changeSelected(null);
      store.changeIsOpen(false);
      navigate('/profile', {
        replace: true,
      });
    };

    const menuItemName = store.selected?.parent?.name || store.selected?.name;
    const subTitle = !!store.selected?.parent?.name ? store.selected?.name : '';

    return (
      <div className={styles.menuSideBar}>
        <nav className={`${styles.nav} cust-mb-4 mb-lg-0 position-relative`}>
          {uiStore.screenWidth <= Breakpoints.DesktopStart && (
            <AccordionItem
              title={menuItemName ? translate(`menu.${menuItemName}`) : ''}
              subTitle={!!subTitle ? translate(`menu.${subTitle}`) : ''}
              iconLeft={store.selected?.parent?.icon || store.selected?.icon}
              iconLeftActive={null}
              iconRightActive={'icon-close'}
              canExpand={true}
              onClick={() => store.changeIsOpen(!store.isOpen)}
              isActive={store.isOpen}
              addActiveClass={false}
              className={`${styles.mobileMenuMain} ${store.isOpen ? styles.expanded : ''}`}
            ></AccordionItem>
          )}
          <div
            className={`${uiStore.screenWidth <= Breakpoints.DesktopStart ? styles.mobileOverlay : ''} ${
              store.isOpen ? styles.expanded : ''
            }`}
          >
            {uiStore.screenWidth <= Breakpoints.DesktopStart && (
              <>
                <div className={styles.overlayGradient}></div>
                <div className={styles.underlayGradient}></div>
              </>
            )}
            <FeaturePanel hasFeature={isAuthenticated && featureStore.hasViewPermission(FeatureFlag.MenuProfileCard)}>
              <ProfileCard
                theme={
                  uiStore.screenWidth <= Breakpoints.DesktopStart ? CardThemeType.Transparent : CardThemeType.DarkGlass
                }
                onClick={handleProfileClick}
              ></ProfileCard>
            </FeaturePanel>
            <Accordion hasGradientBackground={uiStore.screenWidth > Breakpoints.DesktopStart} classes={['cust-mb-1']}>
              {routes
                ?.filter((route) => !route.hideFromMainMenu)
                ?.map((route, i) => (
                  <AccordionItem
                    title={translate(`menu.${route?.name}`)}
                    link={route.path ? `${Util.BaseUrl}#${route.path}` : null}
                    iconLeft={route?.icon}
                    canExpand={route?.children !== undefined && route?.children?.length > 0}
                    key={i}
                    onClick={() => handleMainMenuItemClick(route)}
                    isActive={
                      route.isActive === true ||
                      (route.isActive === undefined && store.activeRoute?.name === route.name)
                    }
                    isSelected={store.selected?.name === route.name || store.selected?.parent?.name === route.name}
                  >
                    {route.children ? (
                      <>
                        {route.children
                          .filter((route) => !route.hideFromMainMenu)
                          .map((subRoute, i) => (
                            <a
                              className={`text-body-small d-block cust-ml-6 cust-pt-4 ${styles.subListItem} ${
                                activeRoute?.name === subRoute?.name ? styles.isActive : ''
                              }`}
                              key={i}
                              onClick={(e) => handleSubMenuItemClick(e, route, subRoute)}
                              href={`${Util.BaseUrl}#${subRoute.path}`}
                            >
                              {translate(`menu.${subRoute?.name}`)}
                            </a>
                          ))}
                      </>
                    ) : null}
                  </AccordionItem>
                ))}
            </Accordion>
            <FeaturePanel
              hasFeature={isAuthenticated && featureStore.hasViewPermission(FeatureFlag.MenuAmountToPayCard)}
            >
              <div className={styles.amountToPayCardContainer}>
                <AmountToPay layout={AmountToPayLayout.CompactCard} />
              </div>
            </FeaturePanel>
            <DevTools></DevTools>
          </div>
        </nav>
      </div>
    );
  }
);